import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  getAllUserNotifications,
  updateAllNotificationReadStatus,
  updateOneNotificationReadStatus,
} from "../api/Api";

export const NotificationContext = createContext(null);

// Default page size for notifications, can be overridden by environment variable
const PAGE_SIZE = 3;

/**
 * Custom hook to access notification context
 * @returns {Object} The notification context containing notification methods
 * @throws {Error} If used outside of NotificationContextProvider
 * @example
 * const { notifications, loading, error, unreadNotificationCount, markNotificationsAsRead, markAllNotificationsAsRead } = useNotification();
 * notifications: {
 *    "id": 2,
 *    "user_id": 1,
 *    "message": "User Y started following you",
 *    "type": "FOLLOW",
 *    "redirect_url": "/profile/user-y",
 *    "is_read": true,
 *    "created_at": "2024-10-16T01:34:51.285Z"
 *}
 */

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotification must be used within a NotificationContextProvider"
    );
  }
  return context;
};

/**
 * NotificationContextProvider component
 * Provides notification data and methods to its children
 * @param {ReactNode} children - The child components to be wrapped by the provider
 * * @returns {JSX.Element} The NotificationContext.Provider component
 * @example
 * <NotificationContextProvider>
 *   <YourComponent />
 * </NotificationContextProvider>
 */

const NotificationContextProvider = ({ children }) => {
  const [loading, setIsLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalItems: 0,
    pageSize: PAGE_SIZE, // Default page size, can be overridden by environment variable
  });
  const {currentPage, pageSize, totalItems} = pagination;

  useEffect(() => {
    loadAllNotifications(currentPage,pageSize);
  }, [currentPage]);

  const loadAllNotifications = (page = 1) => {
    setIsLoading(true);
    // Pass page parameter to API call if your API supports pagination
    getAllUserNotifications(page, pageSize)
      .then((response) => {
        if (response?.status === 200) {
          // console.log("Notifications response:", response.data);
          // Handle different response structures

          setNotifications(response.data.notifications ||[]);
          setUnreadNotificationCount(response.data?.unread_count || 0);
          setPagination(prev => {
            return {
              ...prev,
              currentPage: response?.pagination?.current_page || 1,
              totalItems: response?.pagination?.total_items || 0,
            };
          });
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  // Function to get unread notification count
  const refresh = useCallback(() => {
    loadAllNotifications(currentPage);
  }, [currentPage]);

  //   refresh every 1 minute
  useEffect(() => {
    const interval = setInterval(() => {
      refresh();
    }, 60000); // 1 minute
    return () => clearInterval(interval);
  }, [refresh]);

  const markNotificationsAsRead = (notification_id) => {
    updateOneNotificationReadStatus(notification_id).then((response) => {
      if (response?.status === 200) {
        refresh();
      }
    });
  };

  const markAllNotificationsAsRead = () => {
    updateAllNotificationReadStatus().then((response) => {
      if (response?.status === 200) {
        refresh();
      }
    });
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        loading,
        unreadNotificationCount,
        markNotificationsAsRead,
        markAllNotificationsAsRead,
        refresh,
        pagination,
        setPagination
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContextProvider;
